/*
 * recharge-buy-box-sync.js
 * Holistic Goddess - Purity theme
 *
 * Keeps the theme's Add to Cart button in sync with the ReCharge subscription widget.
 *
 * Why this is needed
 * ------------------
 * The theme renders the button label as "Add to Cart - <price>", where the price lives in
 * <span class="total-price__detail" data-total-price="<unit price in cents>">.
 * theme.js only recalculates that value from the quantity stepper (QuantityInput.updateTotalPrice),
 * and the `data-total-price` attribute is printed once by Liquid at the one-time price.
 * ReCharge rewrites the main product price when a plan is chosen, but knows nothing about the
 * theme's button, so the button keeps showing the one-time price.
 *
 * It also fixes a second problem: ReCharge injects its hidden <input name="selling_plan"> into the
 * main product form only. The sticky Add to Cart bar has no such input, so adding from the sticky
 * bar silently created a ONE-TIME purchase even with "Subscribe & save" selected.
 *
 * What it does
 * ------------
 * 1. Reads the selected selling plan from the main product form (ReCharge's own hidden input -
 *    the value that actually gets submitted, so we can never disagree with what ReCharge does).
 * 2. Mirrors that plan into every other add-to-cart form in the same section (the sticky bar).
 * 3. Looks up the real per-delivery price for that plan from /products/<handle>.js and rewrites
 *    `data-total-price` + the button label in every form in the section.
 *
 * Because it rewrites `data-total-price`, the theme's own quantity handler keeps working
 * unchanged and multiplies from the corrected unit price.
 *
 * Safe by design: if anything cannot be resolved it does nothing and the theme behaves as before.
 */
(function () {
  'use strict';

  /* Deployment marker - check `window.__rcBuyBoxSyncVersion` in the console to
     confirm this file is the one running. */
  window.__rcBuyBoxSyncVersion = '5';

  var productCache = {};

  /* ---------- money ---------- */

  function moneyFormat() {
    try {
      return (window.themeGlobalVariables.settings.money_format) || '${{amount}}';
    } catch (e) {
      return '${{amount}}';
    }
  }

  function separate(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) cents = 0;
    var parts = (cents / 100.0).toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  /* Mirrors Shopify's standard money filters so output matches the rest of the theme. */
  function formatMoney(cents, format) {
    format = format || '${{amount}}';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = separate(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator':
        value = separate(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = separate(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':
        value = separate(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator':
        value = separate(cents, 0, ' ', '.'); break;
      case 'amount_with_space_separator':
        value = separate(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator':
        value = separate(cents, 2, ' ', '.'); break;
      default:
        value = separate(cents, 2, ',', '.');
    }
    return format.replace(match[0], value);
  }

  /* ---------- product data ---------- */

  /*
   * Resolve which product a given add-to-cart form belongs to.
   *
   * The previous logic grabbed the FIRST `/products/` link found in the section. On a
   * product page that link is often an upsell / "complete the look" item, so the button
   * was priced from a DIFFERENT product - that stray product's price was the mystery
   * "$41.00" that showed for BOTH one-time and subscription. Resolve per form instead:
   *   1. an explicit data-product-handle on/around the form,
   *   2. else, only if the form is inside a product card, that card's own product link,
   *   3. else the current product page (location) - authoritative for the main buy box
   *      and the sticky bar, and never fooled by upsell links elsewhere in the section.
   */
  function handleFromHref(href) {
    var part = (href || '').split('/products/')[1];
    if (!part) return null;
    return part.split('?')[0].split('#')[0].split('/')[0];
  }

  function handleForForm(form) {
    var tagged = form.closest('[data-product-handle]');
    if (tagged && tagged.getAttribute('data-product-handle')) {
      return tagged.getAttribute('data-product-handle');
    }
    var card = form.closest('.card-product, [data-product-card], product-card, quick-add-modal');
    if (card) {
      var link = card.querySelector('a[href*="/products/"]');
      if (link) {
        var h = handleFromHref(link.getAttribute('href'));
        if (h) return h;
      }
    }
    if (location.pathname.indexOf('/products/') !== -1) {
      return handleFromHref(location.pathname);
    }
    return null;
  }

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  /* ---------- dom helpers ---------- */

  function cartForms(scope) {
    return Array.prototype.slice.call(scope.querySelectorAll('form[action*="/cart/add"]'))
      .filter(function (f) { return f.querySelector('.total-price__detail'); });
  }

  /* The form ReCharge actually writes its hidden selling_plan input into. */
  function primaryForm(forms) {
    for (var i = 0; i < forms.length; i++) {
      if (!forms[i].classList.contains('form-sticky-add-cart')) return forms[i];
    }
    return forms[0] || null;
  }

  function selectedPlan(form) {
    if (!form) return null;
    var el = form.querySelector('[name="selling_plan"]');
    if (!el || !el.value) return null;
    var id = Number(el.value);
    return id ? id : null;
  }

  /* Keep the sticky bar submitting the same plan as the main form. */
  function mirrorPlan(form, planId) {
    var existing = form.querySelector('input[type="hidden"][name="selling_plan"][data-rc-mirrored]');
    if (planId === null) {
      if (existing) existing.parentNode.removeChild(existing);
      return;
    }
    if (form.querySelector('[name="selling_plan"]:not([data-rc-mirrored])')) return; // ReCharge owns it
    if (!existing) {
      existing = document.createElement('input');
      existing.type = 'hidden';
      existing.name = 'selling_plan';
      existing.setAttribute('data-rc-mirrored', '');
      form.appendChild(existing);
    }
    if (existing.value !== String(planId)) existing.value = String(planId);
  }

  function quantityOf(form) {
    var q = form.querySelector('quantity-input input, [name="quantity"]');
    var n = q ? Number(q.value) : 1;
    return n > 0 ? n : 1;
  }

  function unitPriceFor(product, variantId, planId) {
    var variant = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === variantId) { variant = product.variants[i]; break; }
    }
    if (!variant) variant = product.variants[0];
    if (!variant) return null;
    if (planId) {
      var allocations = variant.selling_plan_allocations || [];
      for (var j = 0; j < allocations.length; j++) {
        if (allocations[j].selling_plan_id === planId) {
          var alloc = allocations[j];
          /* Use the per-unit selling-plan price (allocation.price). This is the
             amount the cart actually charges for the line and the exact value the
             Subscribe & Save widget displays. We deliberately do NOT use
             per_delivery_price: on this store it carries a different discount and
             was the source of the wrong "$41.00" on the button while the widget
             (and the real cart price) showed $42.84. */
          if (alloc.price != null) return alloc.price;
          if (alloc.per_delivery_price != null) return alloc.per_delivery_price;
          return variant.price;
        }
      }
    }
    return variant.price;
  }

  /* Reads a displayed money string back to cents. Assumes a period decimal
     separator (this store's money_format is "${{amount}}"); thousands separators
     and currency symbols are stripped. */
  function centsFromText(text) {
    var cleaned = (text || '').replace(/[^0-9.]/g, '');
    if (!cleaned) return null;
    var value = parseFloat(cleaned);
    if (isNaN(value)) return null;
    return Math.round(value * 100);
  }

  function paint(form, unitPrice) {
    /* Remember the authoritative unit price so the guard can restore it. */
    form._rcUnit = unitPrice;
    var text = formatMoney(unitPrice * quantityOf(form), moneyFormat());
    var nodes = form.querySelectorAll('.total-price__detail');
    for (var i = 0; i < nodes.length; i++) {
      writeNode(nodes[i], unitPrice, text, form);
    }
  }

  /* Writes one price node while its own guard observer is disconnected, so our
     own write can never re-trigger the guard. */
  function writeNode(el, unitPrice, text, form) {
    var attrNeeds = el.getAttribute('data-total-price') !== String(unitPrice);
    var textNeeds = el.textContent.trim() !== text;
    if (attrNeeds || textNeeds) {
      if (el._rcObserver) el._rcObserver.disconnect();
      if (attrNeeds) el.setAttribute('data-total-price', String(unitPrice));
      if (textNeeds) el.textContent = text;
      var button = el.closest('button[name="add"]');
      if (button) {
        var label = button.querySelector('.btn-label');
        if (label) button.setAttribute('aria-label', label.textContent.replace(/\s+/g, ' ').trim());
      }
    }
    guardNode(el, form); /* ensure the guard is attached; no-op if already observing */
  }

  /*
   * Defends the button price. ReCharge's app-embed script (injected via
   * content_for_header, not part of the theme) rewrites this node a moment after
   * we set it, dropping it to per_delivery_price ("$41.00") even though the widget
   * and the real cart line are allocation.price ("$42.84"). This observer restores
   * our value whenever that happens.
   *
   * Quantity changes are left alone: the theme's own handler keeps data-total-price
   * equal to our unit price and only scales the text, so the numbers still agree
   * and no correction is made.
   */
  function guardNode(el, form) {
    if (!el._rcObserver) {
      el._rcObserver = new MutationObserver(function () {
        if (form._rcUnit == null) return;
        var expectedCents = form._rcUnit * quantityOf(form);
        var attrOk = el.getAttribute('data-total-price') === String(form._rcUnit);
        var shown = centsFromText(el.textContent);
        var textOk = shown !== null && Math.abs(shown - expectedCents) <= 1;
        if (attrOk && textOk) return; /* legit state (e.g. quantity) - leave it */
        if (form._rcFixes == null) form._rcFixes = 0;
        if (form._rcFixes > 30) return; /* safety valve against a write war */
        form._rcFixes++;
        writeNode(el, form._rcUnit, formatMoney(expectedCents, moneyFormat()), form);
      });
    }
    el._rcObserver.observe(el, {
      attributes: true,
      attributeFilter: ['data-total-price'],
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  /* ---------- main ---------- */

  /*
   * The shopper's actual choice, read straight from the theme's Subscribe & Save widget
   * UI (the one-time/subscription radio + the frequency <select>) rather than from a
   * hidden selling_plan input. The hidden input is ambiguous here: ReCharge injects its
   * own and it does NOT clear when the shopper switches back to One-time, which left the
   * button stuck on the subscription price. Returns { known, planId }; planId null means
   * one-time.
   */
  function currentSelection(scope) {
    var root = scope || document;
    var widget = root.querySelector('subscription-widget') || document.querySelector('subscription-widget');
    if (!widget) return { known: false, planId: null };
    var checked = widget.querySelector('[data-purchase-type]:checked');
    if (!checked) return { known: false, planId: null };
    if (checked.getAttribute('data-purchase-type') === 'one-time') {
      return { known: true, planId: null };
    }
    var card = checked.closest('[data-subscription-card]');
    var select = card ? card.querySelector('[data-subscription-plan-select]') : null;
    var id = select && select.value ? Number(select.value) : null;
    if (!id) return { known: false, planId: null };
    return { known: true, planId: id };
  }

  function syncScope(scope) {
    var forms = cartForms(scope);
    if (!forms.length) return;

    var primary = primaryForm(forms);
    /* Prefer the widget's real UI state; fall back to the hidden input only if the
       widget cannot be read. */
    var selection = currentSelection(scope);
    var planId = selection.known ? selection.planId : selectedPlan(primary);

    forms.forEach(function (form) {
      if (form !== primary) mirrorPlan(form, planId);
      form._rcFixes = 0; /* fresh correction budget for this interaction */

      var handle = handleForForm(form);
      if (!handle) return;

      getProduct(handle).then(function (product) {
        if (!product || !product.variants || !product.variants.length) return;
        var idInput = form.querySelector('[name="id"]');
        var variantId = idInput ? Number(idInput.value) : product.variants[0].id;
        var unit = unitPriceFor(product, variantId, planId);
        if (unit === null || unit === undefined) return;
        paint(form, unit);
      });
    });
  }

  function scopes() {
    var found = document.querySelectorAll('[id^="shopify-section"]');
    return Array.prototype.slice.call(found).filter(function (s) {
      return s.querySelector('form[action*="/cart/add"] .total-price__detail');
    });
  }

  var pending = null;
  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(function () { scopes().forEach(syncScope); }, 80);
  }

  function onInteraction(event) {
    var t = event.target;
    if (!t) return;
    /* Any change event (variant select, delivery-frequency select, purchase-option
       radio) can move the price, so always reconcile. change events are infrequent and
       schedule() is debounced, so this is cheap. */
    if (event.type === 'change') { schedule(); return; }
    /* Clicks: reconcile when they land in the subscription widget, a product add-to-cart
       form, or the quantity stepper. Card clicks also dispatch a 'change' on the radio,
       which is already caught above. */
    if (t.closest && (
        t.closest('subscription-widget') ||
        t.closest('[data-widget-container]') ||
        t.closest('[data-subscription-card]') ||
        t.closest('quantity-input') ||
        t.closest('form[action*="/cart/add"]'))) {
      schedule();
    }
  }

  function start() {
    document.addEventListener('change', onInteraction, true);
    document.addEventListener('click', onInteraction, true);

    /* ReCharge renders async and re-renders on every change; watch its container only. */
    var observer = new MutationObserver(schedule);
    function observeWidgets() {
      /* Watch ReCharge's native container (if present) AND the theme's custom
         <subscription-widget>, so a programmatic option change also repaints. */
      var widgets = document.querySelectorAll('[data-widget-container], subscription-widget');
      for (var i = 0; i < widgets.length; i++) {
        observer.observe(widgets[i], {
          subtree: true, childList: true, attributes: true,
          attributeFilter: ['value', 'checked', 'class']
        });
      }
    }
    observeWidgets();

    /* Permanent reconciliation loop - this is the guarantee the button always reflects
       the current selection, no matter what triggered the change (a click, a frequency
       change, a section re-render, or a late external write) and even if an event ever
       slips past the listeners above. It is cheap: product data is cached and the button
       is only rewritten when the displayed price actually differs. */
    var tries = 0;
    setInterval(function () {
      tries++;
      if (tries <= 10) observeWidgets(); /* re-attach observers while things settle */
      schedule();
    }, 600);

    document.addEventListener('shopify:section:load', function () { observeWidgets(); schedule(); });
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();